import React, {useMemo, memo} from 'react';

import {useSelector} from 'react-redux';

import {PRODUCT_TYPES} from 'constants/product';

import {CheckoutItemType, CheckoutItemsType} from 'desktop/types/checkoutItemsType';

import {getOrderReviewItems} from '../../store/selectors/orderReviewItems/orderReviewItems';

import {AccessoryProductItem} from './accessoryProductItem/accessoryProductItem';
import {ContactLensesProductItem} from './contactLensesProductItem/contactLensesProductItem';
import {ContactLensesVistaProductItem} from './contactLensesVistaProductItem/contactLensesVistaProductItem';
import {GlassesProductItem} from './glassesProductItem/glassesProductItem';

import s from './cartItemFacelift.scss';

const sortProductsOutOfStock = (items?: CheckoutItemsType) =>
    items?.length
        ? [...items]?.sort(
              (x, y) => Number(y.product.stock.isOutOfStock) - Number(x.product.stock.isOutOfStock),
          )
        : items;

const ProductItem = memo(
    ({arrayLength, index, item}: {item: CheckoutItemType; index: number; arrayLength: number}) => {
        const {brand, type} = item.product;

        if (type === PRODUCT_TYPES.ACCESSORY) {
            return (
                <div aria-label="Accessory item" key={item.id}>
                    <AccessoryProductItem item={item} />
                    {index < arrayLength - 1 && <div className={s.divider} />}
                </div>
            );
        }

        if (type === PRODUCT_TYPES.CONTACT_LENSES) {
            if (brand?.label === 'Vista Plus') {
                return (
                    <div aria-label="Vista Plus item" key={item.id}>
                        <ContactLensesVistaProductItem item={item} />
                        {index < arrayLength - 1 && <div className={s.divider} />}
                    </div>
                );
            }
            return (
                <div aria-label="Lenses item" key={item.id}>
                    <ContactLensesProductItem item={item} />
                    {index < arrayLength - 1 && <div className={s.divider} />}
                </div>
            );
        }

        return (
            <div aria-label="Cart item" key={item.id}>
                <GlassesProductItem item={item} />
                {index < arrayLength - 1 && <div className={s.divider} />}
            </div>
        );
    },
);

ProductItem.displayName = 'ProductItem';

/**
 * Cart item facelift redesign products
 * @returns {React.ReactNode}
 */
export const CartItemFacelift: React.FC = memo((): React.JSX.Element => {
    const items = useSelector(getOrderReviewItems);

    const sortedItems = useMemo(() => sortProductsOutOfStock(items), [items]);

    const productItems = useMemo(() => {
        if (!sortedItems?.length) {
            return null;
        }

        return sortedItems.map((item, index) => (
            <ProductItem arrayLength={sortedItems.length} index={index} item={item} key={item.id} />
        ));
    }, [sortedItems]);

    return (
        <div className={s.container}>
            <div className={s.cartItems}>{productItems}</div>
        </div>
    );
});

CartItemFacelift.displayName = 'CartItemFacelift';
